(() => {
  const roots = document.querySelectorAll('[data-article-personalization-demo]:not([data-initialized="true"])');
  if (!roots.length) return;

  const wait = (milliseconds) => new Promise((resolve) => window.setTimeout(resolve, milliseconds));
  const escapeHtml = (value) => String(value == null ? '' : value).replace(/[&<>"']/g, (character) => ({
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;',
  }[character]));
  const numberValue = (value, fallback) => Number.isFinite(Number(value)) ? Number(value) : fallback;
  const normalizeFunctionName = (value) => String(value || '').trim().toLowerCase().replace(/[ -]+/g, '_');

  roots.forEach((root) => {
    root.dataset.initialized = 'true';
    const configNode = root.querySelector('[data-personalization-demo-config]');
    const initialStateNode = root.querySelector('[data-personalization-demo-initial-state]');
    const controlsNode = root.querySelector('[data-personalization-demo-controls]');
    const previewNode = root.querySelector('[data-personalization-demo-preview]');
    const mainImageNode = root.querySelector('[data-personalization-demo-main-image]');
    const compositionNode = root.querySelector('[data-personalization-demo-composition]');
    const backgroundNode = root.querySelector('[data-personalization-demo-background]');
    const svgNode = root.querySelector('[data-personalization-demo-svg]');
    const previewFocusNode = root.querySelector('[data-personalization-demo-preview-focus]');
    const soundStartButton = root.querySelector('[data-personalization-demo-sound-start]');
    const statusNode = root.querySelector('[data-personalization-demo-status]');
    const cursorNode = root.querySelector('[data-personalization-demo-cursor]');
    const dialogNode = root.querySelector('[role="dialog"]');
    const reopenButton = root.querySelector('[data-personalization-demo-reopen]');
    const ctaButton = root.querySelector('[data-personalization-demo-cta]');
    const ctaLabel = root.querySelector('[data-personalization-demo-cta-label]');
    const productOptionsPanel = root.querySelector('[data-personalization-demo-product-options]');
    const variantsNode = root.querySelector('[data-personalization-demo-variants]');
    const productPriceNode = root.querySelector('[data-personalization-demo-price]');
    const comparePriceNode = root.querySelector('[data-personalization-demo-compare-price]');
    const productPreviewNode = root.querySelector('[data-personalization-demo-product-preview]');
    const cartStatusNode = root.querySelector('[data-personalization-demo-cart-status]');
    const giftButton = root.querySelector('[data-personalization-demo-gift]');
    const giftModal = root.querySelector('[data-personalization-demo-gift-modal]');
    const giftForm = root.querySelector('[data-personalization-demo-gift-form]');
    const giftPromotionApi = 'https://custom.wahup.com/api/storefront/gift-promotions';
    let config = {};
    let savedInitialState = { values: {}, variantId: '' };
    let variants = [];
    let fields = [];
    let state = {};
    let assignedState = {};
    let selectedVariant = null;
    let runToken = 0;
    let open = false;
    let productOptionsOpen = false;
    let addingToCart = false;
    let animationRunning = false;
    let giftSubmitting = false;
    let giftViewportFrame = 0;
    let openTransitionTimer = 0;
    let autoOpenTimer = 0;
    let returnScrollCleanup = () => {};
    let textAutofitCanvas = null;
    let interactionAudioContext = null;
    let interactionSoundBuffer = null;
    let interactionSoundLoad = null;
    let interactionNativeAudio = null;
    let interactionSoundLastStartedAt = 0;
    let interactionSoundUnlockedByGesture = false;
    let previewFocusTimer = 0;
    let previewFocusGeneration = 0;
    const loadedPreviewFonts = new Set();
    const customilyLibraryAssetCache = new Map();
    const previewImageAlphaBoundsCache = new Map();
    const demoSessionId = window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const interactionSoundUrl = String(root.dataset.interactionSoundUrl || '');
    const prefersNativeInteractionAudio = /iP(?:hone|ad|od)/i.test(navigator.userAgent)
      || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    const requiresSoundStartGesture = prefersNativeInteractionAudio
      || window.matchMedia('(hover: none) and (pointer: coarse)').matches;
    const setPreviewStatus = (label) => {
      if (statusNode) statusNode.textContent = label;
    };

    const ensureInteractionAudioContext = () => {
      const AudioContextClass = window.AudioContext || window.webkitAudioContext;
      if (!AudioContextClass) {
        root.dataset.interactionSoundStatus = 'unsupported';
        return null;
      }
      if (!interactionAudioContext || interactionAudioContext.state === 'closed') {
        interactionAudioContext = new AudioContextClass();
      }
      return interactionAudioContext;
    };

    const ensureNativeInteractionAudio = () => {
      if (!interactionSoundUrl) return null;
      if (!interactionNativeAudio) {
        interactionNativeAudio = new Audio(interactionSoundUrl);
        interactionNativeAudio.preload = 'auto';
        interactionNativeAudio.playsInline = true;
        interactionNativeAudio.volume = 1;
      }
      return interactionNativeAudio;
    };

    const recordInteractionSoundPlayed = (engine) => {
      root.dataset.interactionSoundStatus = 'played';
      root.dataset.interactionSoundEngine = engine;
      root.dataset.interactionSoundPlayCount = String(numberValue(root.dataset.interactionSoundPlayCount, 0) + 1);
    };

    const prepareInteractionSound = (audioContext) => {
      if (!audioContext || !interactionSoundUrl) return Promise.resolve(null);
      if (interactionSoundBuffer) return Promise.resolve(interactionSoundBuffer);
      if (!interactionSoundLoad) {
        root.dataset.interactionSoundStatus = 'loading';
        interactionSoundLoad = fetch(interactionSoundUrl, { cache: 'force-cache' })
          .then((response) => {
            if (!response.ok) throw new Error(`Click sound returned ${response.status}`);
            return response.arrayBuffer();
          })
          .then((encoded) => audioContext.decodeAudioData(encoded.slice(0)))
          .then((decoded) => {
            interactionSoundBuffer = decoded;
            root.dataset.interactionSoundStatus = 'ready';
            return decoded;
          })
          .catch(() => {
            root.dataset.interactionSoundStatus = 'error';
            return null;
          })
          .finally(() => {
            interactionSoundLoad = null;
          });
      }
      return interactionSoundLoad;
    };

    const resumeInteractionAudioContext = async () => {
      const audioContext = ensureInteractionAudioContext();
      if (!audioContext) return null;
      if (audioContext.state === 'suspended') {
        try {
          await audioContext.resume();
        } catch (error) {
          return null;
        }
      }
      return audioContext.state === 'running' ? audioContext : null;
    };

    const playInteractionSound = async () => {
      const requestedAt = Date.now();
      if (requestedAt - interactionSoundLastStartedAt < 140) return false;
      interactionSoundLastStartedAt = requestedAt;
      if (prefersNativeInteractionAudio) {
        const nativeAudio = ensureNativeInteractionAudio();
        if (nativeAudio) {
          try {
            nativeAudio.currentTime = 0;
            await nativeAudio.play();
            recordInteractionSoundPlayed('html-audio');
            return true;
          } catch (error) {
            // A prior page touch can unlock WebAudio even when Safari rejects HTMLAudio autoplay.
          }
        }
      }
      const audioContext = await resumeInteractionAudioContext();
      if (!audioContext) {
        interactionSoundLastStartedAt = 0;
        if (root.dataset.interactionSoundStatus !== 'unsupported') root.dataset.interactionSoundStatus = 'blocked';
        return false;
      }
      const buffer = interactionSoundBuffer || await prepareInteractionSound(audioContext);
      if (!buffer || audioContext.state !== 'running') return false;
      const startedAt = audioContext.currentTime + .005;
      const source = audioContext.createBufferSource();
      const gain = audioContext.createGain();
      source.buffer = buffer;
      gain.gain.setValueAtTime(.95, startedAt);
      source.connect(gain);
      gain.connect(audioContext.destination);
      source.addEventListener('ended', () => {
        source.disconnect();
        gain.disconnect();
      }, { once: true });
      source.start(startedAt);
      recordInteractionSoundPlayed('web-audio');
      return true;
    };

    const unlockInteractionSound = () => {
      void resumeInteractionAudioContext().then((audioContext) => (
        audioContext ? prepareInteractionSound(audioContext) : null
      ));
    };

    const playTouchedInteractionSound = (event) => {
      if (
        !prefersNativeInteractionAudio
        || event.target.closest('[data-personalization-demo-sound-start]')
        || !event.target.closest('button, [data-demo-text]')
      ) return;
      void playInteractionSound();
    };

    const fetchWithTimeout = async (url, options, timeoutMilliseconds, timeoutMessage) => {
      const controller = new AbortController();
      const timeout = window.setTimeout(() => controller.abort(), timeoutMilliseconds);
      try {
        return await fetch(url, { ...options, signal: controller.signal });
      } catch (error) {
        if (error?.name === 'AbortError') throw new Error(timeoutMessage);
        throw error;
      } finally {
        window.clearTimeout(timeout);
      }
    };

    try {
      config = JSON.parse(configNode?.textContent || '{}');
    } catch (error) {
      config = {};
    }
    try {
      const parsed = JSON.parse(initialStateNode?.textContent || '{}');
      savedInitialState = parsed && typeof parsed === 'object'
        ? {
            values: parsed.values && typeof parsed.values === 'object' ? parsed.values : {},
            variantId: String(parsed.variantId || '').replace(/\D+/g, ''),
          }
        : { values: {}, variantId: '' };
    } catch (error) {
      savedInitialState = { values: {}, variantId: '' };
    }
    try {
      variants = JSON.parse(variantsNode?.textContent || '[]');
    } catch (error) {
      variants = [];
    }

    const track = (action, details = {}) => {
      const eventName = `personalization_demo_${action}`;
      root.dataset.ga4LastEvent = eventName;
      root.dataset.ga4EventCount = String(numberValue(root.dataset.ga4EventCount, 0) + 1);
      const parameters = {
        article_id: String(root.dataset.articleId || ''),
        article_handle: String(root.dataset.articleHandle || ''),
        article_title: String(root.dataset.articleTitle || ''),
        product_id: String(root.dataset.productId || ''),
        product_handle: String(root.dataset.productHandle || ''),
        product_title: String(root.dataset.productTitle || ''),
        demo_session_id: demoSessionId,
        ...details,
      };
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: eventName,
        ga4_direct_sent: true,
        ...parameters,
      });
      window.gtag = window.gtag || function () {
        window.dataLayer.push(arguments);
      };
      window.gtag('event', eventName, {
        ...parameters,
        send_to: root.dataset.ga4MeasurementId || undefined,
        transport_type: 'beacon',
      });
    };

    const fieldKey = (field) => String(field.id || field.label || '');
    const fieldLabel = (field) => String(field.label || 'Custom option');
    const fieldSearchText = (field) => [
      field?.label,
      field?.name,
      field?.title,
      field?.handle,
      field?.key,
    ].map((value) => String(value || '').toLowerCase()).join(' ');
    const fieldHasKeyword = (field, keyword) => fieldSearchText(field).includes(String(keyword || '').toLowerCase());
    const currentYearValue = () => String(new Date().getFullYear());
    const optionValue = (option) => String(option?.value || option?.label || option?.name || option?.id || '');
    const optionLabel = (option) => String(option?.label || option?.name || option?.value || option?.id || '');
    const optionImage = (option) => String(
      option?.swatchImageUrl
      || option?.thumbnailUrl
      || option?.preview
      || option?.imageUrl
      || option?.url
      || '',
    );
    const optionPrintImage = (option) => {
      const swatch = String(option?.swatchImageUrl || option?.thumbnailUrl || '');
      return [
        option?.originalUrl,
        option?.fullSizeUrl,
        option?.assetUrl,
        option?.url,
        option?.imageUrl,
      ].map((value) => String(value || '')).find((value) => (
        value
        && value !== swatch
        && !/\/(?:preview|thumbnails?)\//i.test(value)
      )) || '';
    };
    const hasChoices = (field) => Array.isArray(field.values) && field.values.length > 0;
    const isUploadField = (field) => ['upload', 'vector-upload', 'multiple-upload'].includes(String(field.type || ''));
    const template = () => config.template || {};
    const allLayers = () => Array.isArray(template().layers) ? template().layers : [];
    const layerBuildYou = (layer) => (
      layer && layer.buildYou && typeof layer.buildYou === 'object' ? layer.buildYou : {}
    );
    const fontFamilyForLayer = (layer) => String(
      layer.fontFamily
      || layer.fontName
      || (Array.isArray(layer.fontOptions) ? layer.fontOptions[0]?.name : '')
      || 'Arial',
    );
    const fontUrlForLayer = (layer) => {
      const options = Array.isArray(layer.fontOptions) ? layer.fontOptions : [];
      const family = fontFamilyForLayer(layer);
      const selected = options.find((font) => (
        String(font.name || '') === family
        || String(font.id || '') === String(layer.fontId || '')
      )) || options.find((font) => font.url);
      return String(layer.fontUrl || selected?.url || '');
    };
    const loadPreviewFonts = async () => {
      if (typeof window.FontFace !== 'function' || !document.fonts) return;
      const promises = allLayers().map(async (layer) => {
        const family = fontFamilyForLayer(layer);
        const url = fontUrlForLayer(layer);
        const key = `${family}|${url}`;
        if (!url || loadedPreviewFonts.has(key)) return;
        loadedPreviewFonts.add(key);
        try {
          const face = new FontFace(family, `url("${url.replace(/"/g, '\\"')}")`);
          const loaded = await face.load();
          document.fonts.add(loaded);
        } catch (error) {
          loadedPreviewFonts.delete(key);
        }
      });
      await Promise.allSettled(promises);
    };
    const fittedLayerFontSize = (layer, text, maximum, family) => {
      const maxSize = Math.max(1, numberValue(maximum, 1));
      const minSize = Math.min(maxSize, Math.max(1, numberValue(layer.minSize, 1)));
      const availableWidth = Math.max(1, numberValue(layer.width, 100)) * .72;
      const availableHeight = Math.max(1, numberValue(layer.height, 100)) * .94;
      const tracking = numberValue(layer.tracking, 0) * .04;
      const label = String(text || '');
      if (!label) return maxSize;
      textAutofitCanvas ||= document.createElement('canvas');
      const context = textAutofitCanvas.getContext('2d');
      if (!context) return maxSize;
      const measuredAt = (fontSize) => {
        context.font = `${layer.textItalic ? 'italic ' : ''}${layer.textBold ? '800' : '400'} ${fontSize}px "${family}", Arial, sans-serif`;
        return Math.max(.001, context.measureText(label).width + Math.max(0, Array.from(label).length - 1) * tracking);
      };
      const fits = (fontSize) => fontSize <= availableHeight && measuredAt(fontSize) <= availableWidth;
      if (fits(maxSize)) return maxSize;
      let low = minSize;
      let high = maxSize;
      for (let index = 0; index < 16; index += 1) {
        const candidate = (low + high) / 2;
        if (fits(candidate)) low = candidate;
        else high = candidate;
      }
      return Math.max(minSize, Math.floor(low * 1000) / 1000);
    };
    const buildYouEllipsePoint = (cx, cy, radiusX, radiusY, angleDegrees) => {
      const angle = (numberValue(angleDegrees, 0) * Math.PI) / 180;
      return {
        x: cx - radiusX * Math.cos(angle),
        y: cy + radiusY * Math.sin(angle),
      };
    };
    const polarPoint = (cx, cy, radius, angleDegrees) => {
      const angle = ((numberValue(angleDegrees, 0) - 90) * Math.PI) / 180;
      return {
        x: cx + radius * Math.cos(angle),
        y: cy + radius * Math.sin(angle),
      };
    };
    const curvedTextPath = (layer, x, y, width, height) => {
      const startAngle = numberValue(layer.curveStringStart, 314.32);
      const endAngle = numberValue(layer.curveStringEnd, 44.95);
      const sweep = layer.curveSide === 'Convex' ? 0 : 1;
      if (layer.buildYouCurvedText === true) {
        const radiusX = width / 2;
        const radiusY = height / 2;
        const start = buildYouEllipsePoint(x, y, radiusX, radiusY, startAngle);
        const end = buildYouEllipsePoint(x, y, radiusX, radiusY, endAngle);
        const delta = (endAngle - startAngle + 360) % 360;
        return `M ${start.x} ${start.y} A ${radiusX} ${radiusY} 0 ${delta > 180 ? 1 : 0} ${sweep} ${end.x} ${end.y}`;
      }
      const radius = Math.max(width, height) * .33;
      const start = polarPoint(x, y, radius, startAngle);
      const end = polarPoint(x, y, radius, endAngle);
      return `M ${start.x} ${start.y} A ${radius} ${radius} 0 1 ${sweep} ${end.x} ${end.y}`;
    };
    const allFields = () => {
      const storefront = Array.isArray(config?.storefront?.fields) ? config.storefront.fields : [];
      const options = Array.isArray(config?.optionSets)
        ? config.optionSets.flatMap((set) => Array.isArray(set.options) ? set.options : [])
        : [];
      const seen = new Set();
      return [...storefront, ...options]
        .filter((field) => {
          const key = fieldKey(field);
          if (!key || seen.has(key) || field.enabled === false) return false;
          seen.add(key);
          return true;
        })
        .sort((left, right) => numberValue(left.position, 0) - numberValue(right.position, 0));
    };

    const choiceForValue = (field, value) => (
      hasChoices(field)
        ? field.values.find((choice) => optionValue(choice) === String(value || ''))
          || field.values.find((choice) => optionLabel(choice).toLowerCase() === String(value || '').toLowerCase())
        : null
    );

    const fallbackTextValue = (field) => {
      const label = fieldLabel(field).toLowerCase();
      if (field.demoText) return String(field.demoText);
      if (label.includes('name')) return 'JENNIFER';
      if (fieldHasKeyword(field, 'year')) return currentYearValue();
      if (label.includes('date')) return currentYearValue();
      if (label.includes('message') || label.includes('quote')) return 'LOVE YOU';
      return 'YOUR TEXT';
    };

    const initialValue = (field) => {
      if (isUploadField(field)) return '';
      if (!hasChoices(field) && fieldHasKeyword(field, 'year')) return currentYearValue();
      if (field.initialValue != null && String(field.initialValue) !== '') {
        if (!hasChoices(field)) return String(field.initialValue);
        const choice = choiceForValue(field, field.initialValue);
        if (choice) return optionValue(choice);
      }
      if (hasChoices(field)) {
        const selected = field.values.find((choice) => choice.selected === true);
        return optionValue(selected || field.values[0]);
      }
      const inputTargetIds = (Array.isArray(field.customilyFunctions) ? field.customilyFunctions : [])
        .filter((fn) => normalizeFunctionName(fn.functionName || fn.type) === 'input_text')
        .map((fn) => String(fn.elementId || fn.targetLayerId || ''))
        .filter(Boolean);
      const targetLayer = allLayers().find((layer) => {
        const buildYou = layerBuildYou(layer);
        const functionalId = String(layer.buildYouFunctionalId || buildYou.functionalId || layer.customilyDisplayId || '');
        return inputTargetIds.includes(functionalId);
      });
      const linkedLayer = targetLayer
        || allLayers().find((layer) => String(layer.id) === String(field.layerId));
      const content = String(linkedLayer?.content || '').trim();
      // Imported Customily fields sometimes point at an adjacent image layer.
      // Do not leak its editor label ("Image"/"Dynamic image") into shopper text.
      return content && !/^(?:image|dynamic image|text)$/i.test(content)
        ? content
        : fallbackTextValue(field);
    };

    const assignedValue = (field) => {
      if (!isUploadField(field) && !hasChoices(field) && fieldHasKeyword(field, 'year')) {
        return currentYearValue();
      }
      const savedValues = savedInitialState.values || {};
      const key = fieldKey(field);
      if (Object.prototype.hasOwnProperty.call(savedValues, key)) {
        const savedValue = String(savedValues[key] == null ? '' : savedValues[key]);
        if (!hasChoices(field) || choiceForValue(field, savedValue)) return savedValue;
      }
      return isUploadField(field) ? '' : initialValue(field);
    };

    const conditionMatches = (condition, visiting = new Set()) => {
      const controller = fields.find((field) => fieldKey(field) === String(condition.optionId || condition.elementId || ''));
      if (controller && !fieldIsActive(controller, visiting)) return false;
      const current = String(state[fieldKey(controller || {})] || '');
      const expected = Array.isArray(condition.values)
        ? condition.values.map(String)
        : String(condition.value || condition.expectedValue || '').split('|').filter(Boolean);
      const operator = String(condition.operator || 'equals').toLowerCase().replace(/\s+/g, '_');
      if (operator === 'is_set') return Boolean(current);
      if (operator === 'not_equals' || operator === 'neq') return !expected.includes(current);
      if (operator === 'contains') return expected.some((value) => current.includes(value));
      return expected.includes(current);
    };

    const fieldIsActive = (field, visiting = new Set()) => {
      const conditions = Array.isArray(field.conditions) ? field.conditions : [];
      if (!conditions.length) return true;
      const key = fieldKey(field);
      if (visiting.has(key)) return true;
      const branch = new Set(visiting);
      branch.add(key);
      const matches = conditions.map((condition) => {
        const matched = conditionMatches(condition, branch);
        return String(condition.action || 'Show').toLowerCase() === 'hide' ? !matched : matched;
      });
      return conditions.some((condition) => String(condition.logic || '').toUpperCase() === 'OR')
        ? matches.some(Boolean)
        : matches.every(Boolean);
    };

    const activeFields = () => fields.filter((field) => (
      field.enabled !== false
      && fieldIsActive(field)
    ));
    const visibleFields = () => activeFields().filter((field) => (
      !field.hiddenFromShopper
      && !String(field.sourceLabel || '').toLowerCase().includes('variant_selector')
    ));

    const functionMapping = (fn, currentValue) => (
      Array.isArray(fn?.values)
        ? fn.values.find((mapping) => String(mapping?.value || '') === String(currentValue || ''))
        : null
    );

    const runtimeContext = () => {
      // Hidden variant controllers are still active personalization functions.
      // Excluding them drops the clean garment/background layer from imported
      // Customily products and leaves the already-personalized Shopify media
      // underneath the animated preview.
      const activeFieldKeys = new Set(activeFields().map(fieldKey));
      const activeGroups = new Set();
      const controlledGroups = new Set();
      const targetFunctions = new Map();
      const allTargetFunctions = new Map();
      const modifierFunctions = new Map();
      let activeTemplateId = '';
      let hasGroupController = false;
      fields.forEach((field) => {
        const active = fieldIsActive(field);
        const current = state[fieldKey(field)];
        (Array.isArray(field.customilyFunctions) ? field.customilyFunctions : []).forEach((fn) => {
          const name = normalizeFunctionName(fn.functionName || fn.type);
          const target = String(fn.elementId || fn.targetLayerId || '');
          if (target && ['pick_clip_art', 'pick_photo', 'input_text'].includes(name)) {
            allTargetFunctions.set(target, [...(allTargetFunctions.get(target) || []), { field, fn }]);
            if (active && activeFieldKeys.has(fieldKey(field))) {
              targetFunctions.set(target, [...(targetFunctions.get(target) || []), { field, fn }]);
            }
          }
          if (target && ['change_text_color', 'change_fill_color'].includes(name) && active) {
            modifierFunctions.set(target, [...(modifierFunctions.get(target) || []), { field, fn }]);
          }
          if (!active) return;
          const mapping = functionMapping(fn, current);
          if (name === 'show_layout') {
            hasGroupController = true;
            (Array.isArray(fn.values) ? fn.values : []).forEach((candidate) => {
              if (candidate?.mappingValue != null) controlledGroups.add(String(candidate.mappingValue));
            });
            if (mapping?.mappingValue != null) activeGroups.add(String(mapping.mappingValue));
          }
          if (name === 'change_template' && mapping && !activeTemplateId) {
            activeTemplateId = String(mapping.mappingValue || '');
          }
        });
      });
      if (!activeTemplateId) {
        const defaultTemplateLayer = allLayers().find((layer) => (
          (layer.buildYouDefaultTemplate || layerBuildYou(layer).defaultTemplate)
          && (layer.buildYouTemplateId || layerBuildYou(layer).templateId)
        )) || allLayers().find((layer) => (
          (layer.buildYouTemplateId || layerBuildYou(layer).templateId)
          && layer.defaultLayoutId != null
          && String(layer.defaultLayoutId) === String(layer.layerLayoutId)
        ));
        activeTemplateId = String(
          defaultTemplateLayer?.buildYouTemplateId
          || layerBuildYou(defaultTemplateLayer).templateId
          || '',
        );
      }
      return {
        activeGroups,
        activeTemplateId,
        allTargetFunctions,
        controlledGroups,
        hasGroupController,
        modifierFunctions,
        targetFunctions,
      };
    };

    const imageLibraries = () => {
      const optionSets = template().imageLibraryOptionSets || {};
      return new Map(
        (Array.isArray(template().imageLibraries) ? template().imageLibraries : [])
          .map((library) => [
            String(library.id || ''),
            Array.isArray(library.options)
              ? library.options
              : Array.isArray(optionSets[library.optionsRef]) ? optionSets[library.optionsRef] : [],
          ]),
      );
    };

    const customilyAssetUrl = (path) => {
      const value = String(path || '').trim();
      if (!value) return '';
      if (/^https?:\/\//i.test(value)) return value;
      return `https://app.customily.com${value.startsWith('/') ? '' : '/'}${value}`;
    };

    const customilyLayerRequiresHydration = (field, layer) => {
      const buildYou = layerBuildYou(layer);
      const provider = String(layer?.buildYouProvider || buildYou.provider || '').trim().toLowerCase();
      const sourceLabel = String(field?.sourceLabel || '').trim().toLowerCase();
      const libraryId = String(layer?.dynamicImageLibraryId || '');
      return /^\d+$/.test(libraryId) && (
        provider === 'customily'
        || sourceLabel.startsWith('customily storefront:')
      );
    };

    const libraryChoiceForMapping = (layer, mapping, libraries = imageLibraries()) => {
      const libraryId = String(layer?.dynamicImageLibraryId || '');
      const mappedOptionId = String(mapping?.mappingValue || '');
      if (!libraryId || !mappedOptionId) return null;
      const options = libraries.get(libraryId) || [];
      return options.find((item) => String(item?.optionId || '') === mappedOptionId) || null;
    };

    const selectedCustomilyLibraryAsset = (field, layer) => {
      if (!field || !layer || isUploadField(field)) return null;
      const fn = (Array.isArray(field.customilyFunctions) ? field.customilyFunctions : [])
        .find((candidate) => normalizeFunctionName(candidate.functionName || candidate.type) === 'pick_clip_art');
      const current = String(state[fieldKey(field)] || '');
      const mapping = functionMapping(fn, current);
      const libraryId = String(layer.dynamicImageLibraryId || '');
      const mappedOptionId = String(mapping?.mappingValue || '');
      const libraryChoice = libraryChoiceForMapping(layer, mapping);
      const selected = choiceForValue(field, current);
      const declaredProviderOptionId = String(selected?.providerOptionId || selected?.optionId || '');
      const providerOptionId = /^\d+$/.test(declaredProviderOptionId)
        ? declaredProviderOptionId
        : /^\d+$/.test(mappedOptionId) ? mappedOptionId : '';
      if (
        !libraryId
        || !mappedOptionId
        || !libraryChoice
        || !providerOptionId
        || !customilyLayerRequiresHydration(field, layer)
      ) return null;
      return {
        key: `${libraryId}:${mappedOptionId}`,
        libraryId,
        providerOptionId,
      };
    };

    const hydrateSelectedCustomilyAssets = async () => {
      const requests = [];
      activeFields().forEach((field) => {
        const targetIds = new Set(
          (Array.isArray(field.customilyFunctions) ? field.customilyFunctions : [])
            .filter((fn) => normalizeFunctionName(fn.functionName || fn.type) === 'pick_clip_art')
            .map((fn) => String(fn.elementId || fn.targetLayerId || ''))
            .filter(Boolean),
        );
        if (!targetIds.size) return;
        allLayers().forEach((layer) => {
          const buildYou = layerBuildYou(layer);
          const functionalId = String(layer.buildYouFunctionalId || buildYou.functionalId || layer.customilyDisplayId || '');
          if (!targetIds.has(functionalId)) return;
          const selected = selectedCustomilyLibraryAsset(field, layer);
          if (!selected) return;
          const cached = customilyLibraryAssetCache.get(selected.key);
          if (typeof cached === 'string' && cached) return;
          if (cached && typeof cached.then === 'function') {
            requests.push(cached);
            return;
          }
          const request = fetch(
            `https://custom.wahup.com/api/storefront/customily-library-assets/${encodeURIComponent(selected.libraryId)}/${encodeURIComponent(selected.providerOptionId)}`,
            { mode: 'cors' },
          ).then((response) => response.ok ? response.json() : null)
            .then((payload) => {
              const assetUrl = customilyAssetUrl(payload?.url || payload?.Path || payload?.path);
              if (assetUrl) customilyLibraryAssetCache.set(selected.key, assetUrl);
              else customilyLibraryAssetCache.delete(selected.key);
            })
            .catch(() => customilyLibraryAssetCache.delete(selected.key));
          customilyLibraryAssetCache.set(selected.key, request);
          requests.push(request);
        });
      });
      await Promise.all(requests);
    };

    const resolvedLayers = () => {
      const context = runtimeContext();
      const libraries = imageLibraries();
      return allLayers().filter((sourceLayer) => {
        const buildYou = layerBuildYou(sourceLayer);
        const templateId = String(sourceLayer.buildYouTemplateId || buildYou.templateId || '');
        if (context.activeTemplateId && templateId && context.activeTemplateId !== templateId) return false;
        return true;
      }).map((sourceLayer) => {
        const layer = { ...sourceLayer };
        const buildYou = layerBuildYou(layer);
        const functionalId = String(layer.buildYouFunctionalId || buildYou.functionalId || layer.customilyDisplayId || '');
        const templateId = String(layer.buildYouTemplateId || buildYou.templateId || '');
        const groupId = String(layer.buildYouGroupFunctionalId || buildYou.groupFunctionalId || '');
        const groupIds = (Array.isArray(layer.buildYouGroupFunctionalIds)
          ? layer.buildYouGroupFunctionalIds
          : Array.isArray(buildYou.groupFunctionalIds) ? buildYou.groupFunctionalIds : [groupId])
          .map(String)
          .filter(Boolean);
        const hasBuildYouVisibility = Object.prototype.hasOwnProperty.call(layer, 'buildYouSourceVisible');
        const hasNestedBuildYouVisibility = Object.prototype.hasOwnProperty.call(buildYou, 'sourceVisible');
        let visible = hasBuildYouVisibility
          ? layer.buildYouSourceVisible !== false
          : hasNestedBuildYouVisibility ? buildYou.sourceVisible !== false : !layer.hidden;
        if (context.activeTemplateId && templateId && context.activeTemplateId !== templateId) visible = false;
        if (
          context.hasGroupController
          && groupIds.some((id) => context.controlledGroups.has(id) && !context.activeGroups.has(id))
        ) visible = false;
        const controllers = functionalId ? context.allTargetFunctions.get(functionalId) || [] : [];
        let applied = false;
        (functionalId ? context.targetFunctions.get(functionalId) || [] : []).forEach(({ field, fn }) => {
          const current = state[fieldKey(field)];
          const name = normalizeFunctionName(fn.functionName || fn.type);
          if (name === 'input_text') {
            layer.content = current;
            applied = true;
            return;
          }
          if (name === 'pick_photo') {
            layer.assetUrl = current;
            applied = Boolean(current);
            return;
          }
          const mapping = functionMapping(fn, current);
          if (name !== 'pick_clip_art' || !mapping) return;
          if (!layer.dynamicImageLibraryId && layer.assetUrl) {
            // Legacy Customily snapshots can persist the selected full-size
            // component directly on the layer without retaining its library.
            // Keep that authoritative asset when its controller branch is active.
            applied = true;
            return;
          }
          const choice = libraryChoiceForMapping(layer, mapping, libraries);
          if (!choice) return;
          const selected = choiceForValue(field, current);
          const cacheKey = `${String(layer.dynamicImageLibraryId || '')}:${String(mapping.mappingValue || '')}`;
          const cachedAsset = customilyLibraryAssetCache.get(cacheKey);
          const requiresHydration = customilyLayerRequiresHydration(field, layer);
          const printAsset = typeof cachedAsset === 'string' && cachedAsset
            ? cachedAsset
            : requiresHydration ? '' : optionPrintImage(choice) || optionPrintImage(selected);
          if (printAsset) {
            layer.assetUrl = printAsset;
            applied = Boolean(layer.assetUrl);
          }
        });
        (functionalId ? context.modifierFunctions.get(functionalId) || [] : []).forEach(({ field, fn }) => {
          const mapping = functionMapping(fn, state[fieldKey(field)]);
          if (mapping?.mappingValue != null) layer.fill = String(mapping.mappingValue);
        });
        if (controllers.length && !applied) visible = false;
        return { ...layer, hidden: !visible };
      }).filter((layer) => !layer.hidden)
        .map((layer, index) => ({
          index,
          layer,
          order: layer.layerTreeOrder == null ? NaN : Number(layer.layerTreeOrder),
        }))
        .sort((left, right) => (
          (Number.isFinite(left.order) ? left.order : left.index)
          - (Number.isFinite(right.order) ? right.order : right.index)
          || left.index - right.index
        ))
        .map(({ layer }) => layer);
    };

    const clamp = (value, minimum, maximum) => Math.min(maximum, Math.max(minimum, value));
    const previewLayerKey = (layer, index) => String(layer.id ?? `${layer.layerTreeOrder ?? index}:${layer.buildYouFunctionalId || ''}`);
    const previewLayerSignature = (layer) => JSON.stringify([
      layer.assetUrl || layer.url || '',
      layer.content || '',
      layer.fill || '',
      layer.x,
      layer.y,
      layer.width,
      layer.height,
      layer.rotation,
      layer.opacity,
    ]);

    const changedPreviewLayers = (beforeLayers, afterLayers) => {
      const before = new Map(beforeLayers.map((layer, index) => [previewLayerKey(layer, index), layer]));
      const after = new Map(afterLayers.map((layer, index) => [previewLayerKey(layer, index), layer]));
      return [...new Set([...before.keys(), ...after.keys()])].flatMap((key) => {
        const previous = before.get(key);
        const next = after.get(key);
        if (previous && next && previewLayerSignature(previous) === previewLayerSignature(next)) return [];
        return [next || previous].filter(Boolean);
      });
    };

    const previewLayerGeometry = (layer, artboardWidth, artboardHeight) => {
      const layerWidth = Math.max(1, numberValue(layer.width, 1));
      const layerHeight = Math.max(1, numberValue(layer.height, 1));
      const centerX = numberValue(layer.x, artboardWidth / 2);
      const centerY = numberValue(layer.y, artboardHeight / 2);
      return {
        layer,
        alphaApplied: false,
        width: layerWidth,
        height: layerHeight,
        x: centerX,
        y: centerY,
        area: layerWidth * layerHeight,
        distanceToCenter: Math.hypot(
          (centerX - artboardWidth / 2) / artboardWidth,
          (centerY - artboardHeight / 2) / artboardHeight,
        ),
      };
    };

    const previewImageAlphaBounds = (url) => {
      const source = String(url || '');
      if (!source) return Promise.resolve(null);
      if (previewImageAlphaBoundsCache.has(source)) return previewImageAlphaBoundsCache.get(source);
      const pending = new Promise((resolve) => {
        const image = new Image();
        image.crossOrigin = 'anonymous';
        image.onload = () => {
          try {
            const naturalWidth = Math.max(1, image.naturalWidth || 1);
            const naturalHeight = Math.max(1, image.naturalHeight || 1);
            const scale = Math.min(1, 192 / Math.max(naturalWidth, naturalHeight));
            const sampleWidth = Math.max(1, Math.round(naturalWidth * scale));
            const sampleHeight = Math.max(1, Math.round(naturalHeight * scale));
            const canvas = document.createElement('canvas');
            canvas.width = sampleWidth;
            canvas.height = sampleHeight;
            const context = canvas.getContext('2d', { willReadFrequently: true });
            context.drawImage(image, 0, 0, sampleWidth, sampleHeight);
            const pixels = context.getImageData(0, 0, sampleWidth, sampleHeight).data;
            let left = sampleWidth;
            let right = -1;
            let top = sampleHeight;
            let bottom = -1;
            for (let y = 0; y < sampleHeight; y += 1) {
              for (let x = 0; x < sampleWidth; x += 1) {
                if (pixels[(y * sampleWidth + x) * 4 + 3] <= 20) continue;
                left = Math.min(left, x);
                right = Math.max(right, x);
                top = Math.min(top, y);
                bottom = Math.max(bottom, y);
              }
            }
            resolve(right >= left && bottom >= top ? {
              left: left / sampleWidth,
              right: (right + 1) / sampleWidth,
              top: top / sampleHeight,
              bottom: (bottom + 1) / sampleHeight,
            } : null);
          } catch (error) {
            resolve(null);
          }
        };
        image.onerror = () => resolve(null);
        image.src = source;
      });
      previewImageAlphaBoundsCache.set(source, pending);
      return pending;
    };

    const visualPreviewLayerGeometry = async (layer, artboardWidth, artboardHeight) => {
      const geometry = previewLayerGeometry(layer, artboardWidth, artboardHeight);
      const source = String(layer.assetUrl || layer.url || '');
      const alpha = await previewImageAlphaBounds(source);
      if (!alpha) return geometry;
      const layerLeft = geometry.x - geometry.width / 2;
      const layerTop = geometry.y - geometry.height / 2;
      const visualWidth = Math.max(1, geometry.width * (alpha.right - alpha.left));
      const visualHeight = Math.max(1, geometry.height * (alpha.bottom - alpha.top));
      const visualX = layerLeft + geometry.width * (alpha.left + alpha.right) / 2;
      const visualY = layerTop + geometry.height * (alpha.top + alpha.bottom) / 2;
      return {
        ...geometry,
        alphaApplied: true,
        width: visualWidth,
        height: visualHeight,
        x: visualX,
        y: visualY,
        area: visualWidth * visualHeight,
        distanceToCenter: Math.hypot(
          (visualX - artboardWidth / 2) / artboardWidth,
          (visualY - artboardHeight / 2) / artboardHeight,
        ),
      };
    };

    const representativePreviewFocusLayers = (geometries) => {
      if (geometries.length < 2) return geometries;
      const maximumArea = Math.max(...geometries.map(({ area }) => area));
      const anchor = geometries
        .sort((left, right) => right.area - left.area || left.distanceToCenter - right.distanceToCenter)[0];
      if (!anchor) return geometries;
      const cluster = geometries.filter((geometry) => {
        const gapX = Math.max(0, Math.abs(geometry.x - anchor.x) - (geometry.width + anchor.width) / 2);
        const gapY = Math.max(0, Math.abs(geometry.y - anchor.y) - (geometry.height + anchor.height) / 2);
        return geometry.area >= maximumArea * .08
          && gapX <= Math.max(geometry.width, anchor.width) * .12
          && gapY <= Math.max(geometry.height, anchor.height) * .12;
      });
      return cluster.length ? cluster : [anchor];
    };

    const focusChangedPreviewLayers = async (layers) => {
      if (!previewNode || !previewFocusNode || !Array.isArray(layers) || !layers.length) return;
      const generation = ++previewFocusGeneration;
      const artboard = template().artboardSize || {};
      const width = Math.max(20, numberValue(artboard.width, 800));
      const height = Math.max(20, numberValue(artboard.height, 800));
      const geometries = await Promise.all(layers.map((layer) => visualPreviewLayerGeometry(layer, width, height)));
      if (generation !== previewFocusGeneration) return;
      const focusLayers = representativePreviewFocusLayers(geometries);
      const bounds = focusLayers.reduce((result, geometry) => {
        const { width: layerWidth, height: layerHeight, x: centerX, y: centerY } = geometry;
        result.left = Math.min(result.left, centerX - layerWidth / 2);
        result.right = Math.max(result.right, centerX + layerWidth / 2);
        result.top = Math.min(result.top, centerY - layerHeight / 2);
        result.bottom = Math.max(result.bottom, centerY + layerHeight / 2);
        return result;
      }, { left: width, right: 0, top: height, bottom: 0 });
      const paddingX = width * .022;
      const paddingY = height * .022;
      const left = clamp(bounds.left - paddingX, 0, width);
      const right = clamp(bounds.right + paddingX, 0, width);
      const top = clamp(bounds.top - paddingY, 0, height);
      const bottom = clamp(bounds.bottom + paddingY, 0, height);
      const radiusX = clamp((right - left) / (2 * width), .075, .4);
      const radiusY = clamp((bottom - top) / (2 * height), .075, .4);
      const x = clamp((left + right) / (2 * width), 0, 1);
      const y = clamp((top + bottom) / (2 * height), 0, 1);
      const coverage = ((right - left) * (bottom - top)) / (width * height);
      const fittedZoom = Math.min(1 / (radiusX * 2), 1 / (radiusY * 2));
      const zoom = coverage > .7 ? 1.3 : clamp(fittedZoom, 1.9, 2.6);
      const maximumPan = Math.max(0, (zoom - 1) / 2);
      const panX = clamp((.5 - x) * zoom, -maximumPan, maximumPan);
      const panY = clamp((.5 - y) * zoom, -maximumPan, maximumPan);
      const screenX = clamp(.5 + (x - .5) * zoom + panX, 0, 1);
      const screenY = clamp(.5 + (y - .5) * zoom + panY, 0, 1);
      previewNode.style.setProperty('--article-preview-focus-pan-x', `${panX * 100}%`);
      previewNode.style.setProperty('--article-preview-focus-pan-y', `${panY * 100}%`);
      previewNode.style.setProperty('--article-preview-focus-zoom', String(zoom));
      previewNode.style.setProperty('--article-preview-focus-x', `${screenX * 100}%`);
      previewNode.style.setProperty('--article-preview-focus-y', `${screenY * 100}%`);
      previewNode.style.setProperty('--article-preview-focus-width', `${radiusX * zoom * 200}%`);
      previewNode.style.setProperty('--article-preview-focus-height', `${radiusY * zoom * 200}%`);
      previewNode.classList.remove('is-focusing');
      void previewNode.offsetWidth;
      previewNode.classList.add('is-focusing');
      root.dataset.previewFocusStatus = 'active';
      root.dataset.previewFocusCount = String(numberValue(root.dataset.previewFocusCount, 0) + 1);
      root.dataset.previewFocusZoom = String(zoom);
      root.dataset.previewFocusLayerCount = String(focusLayers.length);
      root.dataset.previewChangedLayerCount = String(layers.length);
      root.dataset.previewFocusCenter = `${x.toFixed(4)},${y.toFixed(4)}`;
      root.dataset.previewFocusGeometry = focusLayers.some(({ alphaApplied }) => alphaApplied)
        ? 'visible-alpha'
        : 'layer-box';
      window.clearTimeout(previewFocusTimer);
      previewFocusTimer = window.setTimeout(() => {
        previewNode.classList.remove('is-focusing');
        root.dataset.previewFocusStatus = 'idle';
      }, 1020);
    };

    const renderPreview = (label = '', suppliedLayers = null) => {
      const artboard = template().artboardSize || {};
      const width = Math.max(20, numberValue(artboard.width, 800));
      const height = Math.max(20, numberValue(artboard.height, 800));
      svgNode.setAttribute('viewBox', `0 0 ${width} ${height}`);
      const previewBackgroundMode = String(template().previewBackgroundMode || '').toLowerCase();
      const previewBackground = previewBackgroundMode === 'embedded-scene'
        ? ''
        : String(
            selectedVariant?.featured_image?.src
            || selectedVariant?.featured_media?.preview_image?.src
            || template().previewBackgroundImage
            || '',
          );
      backgroundNode.hidden = !previewBackground;
      if (previewBackground) backgroundNode.src = previewBackground;
      const layers = Array.isArray(suppliedLayers) ? suppliedLayers : resolvedLayers();
      svgNode.innerHTML = layers.map((layer, index) => {
        const x = numberValue(layer.x, width / 2);
        const y = numberValue(layer.y, height / 2);
        const layerWidth = Math.max(1, numberValue(layer.width, width));
        const layerHeight = Math.max(1, numberValue(layer.height, height));
        const rotation = numberValue(layer.rotation, 0);
        const opacity = Math.max(0, Math.min(1, numberValue(layer.opacity, 100) / 100));
        const transform = rotation ? ` transform="rotate(${rotation} ${x} ${y})"` : '';
        if (['text', 'curved-text', 'warped-text'].includes(String(layer.kind || ''))) {
          const content = escapeHtml(layer.content || '');
          if (!content) return '';
          const fill = escapeHtml(layer.fill || '#111');
          const rawFontFamily = fontFamilyForLayer(layer);
          const maximumFontSize = Math.max(8, numberValue(layer.maxSize || layer.currentFontSize || layer.fontSize, 30));
          const fontSize = fittedLayerFontSize(layer, layer.content || '', maximumFontSize, rawFontFamily);
          const fontFamily = escapeHtml(rawFontFamily);
          const fontWeight = layer.textBold ? 700 : numberValue(layer.fontWeight, 400);
          const fontStyle = layer.textItalic ? 'italic' : 'normal';
          const letterSpacing = numberValue(layer.tracking, 0) * .04;
          if (String(layer.kind || '') === 'curved-text') {
            const pathId = `article-demo-curve-${String(layer.id || index).replace(/[^a-zA-Z0-9_-]/g, '-')}-${index}`;
            const path = curvedTextPath(layer, x, y, layerWidth, layerHeight);
            return `<defs><path id="${pathId}" d="${path}"></path></defs><text text-anchor="middle" fill="${fill}" opacity="${opacity}" font-family="${fontFamily}, Arial, sans-serif" font-size="${fontSize}" font-weight="${fontWeight}" font-style="${fontStyle}" letter-spacing="${letterSpacing}"${transform}><textPath href="#${pathId}" startOffset="50%" text-anchor="middle">${content}</textPath></text>`;
          }
          return `<text x="${x}" y="${y}" text-anchor="middle" dominant-baseline="middle" fill="${fill}" opacity="${opacity}" font-family="${fontFamily}, Arial, sans-serif" font-size="${fontSize}" font-weight="${fontWeight}" font-style="${fontStyle}" letter-spacing="${letterSpacing}"${transform}>${content}</text>`;
        }
        const url = String(layer.assetUrl || layer.url || '');
        if (!url) return '';
        const cropX = numberValue(layer.cropX, 50);
        const cropY = numberValue(layer.cropY, 50);
        const horizontal = cropX <= 33 ? 'xMin' : cropX >= 67 ? 'xMax' : 'xMid';
        const vertical = cropY <= 33 ? 'YMin' : cropY >= 67 ? 'YMax' : 'YMid';
        const sourceObjectFit = String(layer.sourceObjectFit || '').toLowerCase();
        const mode = sourceObjectFit === 'contain'
          ? 'meet'
          : sourceObjectFit === 'cover'
            ? 'slice'
            : layer.coverMaskArea || ['Fill frame', 'Allow customer crop'].includes(layer.cropMode)
              ? 'slice'
              : 'meet';
        const preserve = `${horizontal}${vertical} ${mode}`;
        return `<image href="${escapeHtml(url)}" x="${x - layerWidth / 2}" y="${y - layerHeight / 2}" width="${layerWidth}" height="${layerHeight}" opacity="${opacity}" preserveAspectRatio="${preserve}"${transform}></image>`;
      }).join('');
      setPreviewStatus(label || 'Preview updated');
    };

    const renderControls = () => {
      controlsNode.innerHTML = visibleFields().map((field, index) => {
        const key = fieldKey(field);
        const label = escapeHtml(fieldLabel(field));
        const fieldHeader = `<div class="article-personalization-demo__label">${label}<span>Step ${index + 1}</span></div>`;
        if (isUploadField(field)) {
          return `<section class="article-personalization-demo__field" data-demo-field="${escapeHtml(key)}">${fieldHeader}<button class="article-personalization-demo__upload" type="button" data-demo-upload="${escapeHtml(key)}">Select a photo</button></section>`;
        }
        if (hasChoices(field)) {
          const choices = field.values.filter((choice) => !choice.hidden).map((choice) => {
            const value = optionValue(choice);
            const choiceLabel = optionLabel(choice);
            const image = optionImage(choice);
            const visual = Boolean(image || choice.color);
            return `<button class="article-personalization-demo__choice${visual ? ' is-visual' : ''}${state[key] === value ? ' is-selected' : ''}" type="button" data-demo-choice="${escapeHtml(key)}" data-value="${escapeHtml(value)}" aria-label="${escapeHtml(choiceLabel)}" title="${escapeHtml(choiceLabel)}"${visual ? ` style="--demo-choice-color:${escapeHtml(choice.color || '#fff')}"` : ''}>${image ? `<img src="${escapeHtml(image)}" alt="">` : escapeHtml(choiceLabel)}</button>`;
          }).join('');
          return `<section class="article-personalization-demo__field" data-demo-field="${escapeHtml(key)}">${fieldHeader}<div class="article-personalization-demo__choices" role="radiogroup" aria-label="${label}">${choices}</div></section>`;
        }
        return `<section class="article-personalization-demo__field" data-demo-field="${escapeHtml(key)}">${fieldHeader}<input class="article-personalization-demo__text" type="text" data-demo-text="${escapeHtml(key)}" value="${escapeHtml(state[key] || '')}" aria-label="${label}"></section>`;
      }).join('');
    };

    const syncControls = (key) => {
      root.querySelectorAll(`[data-demo-choice="${CSS.escape(key)}"]`).forEach((button) => {
        button.classList.toggle('is-selected', button.dataset.value === String(state[key] || ''));
      });
      const input = root.querySelector(`[data-demo-text="${CSS.escape(key)}"]`);
      if (input && input.value !== String(state[key] || '')) input.value = state[key] || '';
    };

    const setValue = (key, value, label, focusChange = true) => {
      const layersBefore = resolvedLayers();
      const visibleBefore = visibleFields().map(fieldKey).join('\u0000');
      state[key] = value;
      const layersAfter = resolvedLayers();
      const visibleAfter = visibleFields().map(fieldKey).join('\u0000');
      // A layout controller can reveal or hide dependent fields. Rebuild the
      // controls so the blog demo follows the same conditional flow as product.
      if (visibleBefore !== visibleAfter) renderControls();
      else syncControls(key);
      renderPreview(label, layersAfter);
      if (focusChange) void focusChangedPreviewLayers(changedPreviewLayers(layersBefore, layersAfter));
    };

    const setValueAndHydrate = async (key, value, label) => {
      const layersBefore = resolvedLayers();
      setValue(key, value, label, false);
      await hydrateSelectedCustomilyAssets();
      const layersAfter = resolvedLayers();
      renderPreview(label, layersAfter);
      await focusChangedPreviewLayers(changedPreviewLayers(layersBefore, layersAfter));
    };

    const productOptionGroups = () => (
      [...root.querySelectorAll('[data-personalization-demo-product-option]')]
        .sort((left, right) => numberValue(left.dataset.personalizationDemoProductOption, 0)
          - numberValue(right.dataset.personalizationDemoProductOption, 0))
    );

    const selectedProductOptions = () => productOptionGroups().map((group) => (
      group.querySelector('[data-personalization-demo-product-value].is-selected')?.dataset.personalizationDemoProductValue || ''
    ));

    const applySavedVariant = () => {
      if (!savedInitialState.variantId) return;
      const savedVariant = variants.find((variant) => (
        String(variant?.id || '').split('/').pop() === savedInitialState.variantId
      ));
      if (!savedVariant) return;
      productOptionGroups().forEach((group, index) => {
        const selectedValue = String(savedVariant.options?.[index] || '');
        group.querySelectorAll('[data-personalization-demo-product-value]').forEach((button) => {
          const selected = button.dataset.personalizationDemoProductValue === selectedValue;
          button.classList.toggle('is-selected', selected);
          button.setAttribute('aria-pressed', String(selected));
        });
      });
    };

    const selectedProductOptionDetails = () => productOptionGroups().map((group) => ({
      name: group.querySelector('legend')?.textContent?.trim() || 'Option',
      value: group.querySelector('[data-personalization-demo-product-value].is-selected')?.dataset.personalizationDemoProductValue || '',
    })).filter((option) => option.value);

    const displayedFieldValue = (field) => {
      const value = String(state[fieldKey(field)] || '');
      const choice = choiceForValue(field, value);
      return choice ? optionLabel(choice) : value;
    };

    const personalizationOptionDetails = () => visibleFields()
      .filter((field) => !isUploadField(field) && displayedFieldValue(field))
      .map((field) => ({
        name: fieldLabel(field),
        value: displayedFieldValue(field),
      }));

    const createPersonalization = async () => {
      const response = await fetchWithTimeout('https://custom.wahup.com/api/storefront/personalizations', {
        method: 'POST',
        mode: 'cors',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          shop: root.dataset.shop,
          productHandle: root.dataset.productHandle,
          variantId: String(selectedVariant?.id || ''),
          templateId: String(template().id || ''),
          values: state,
        }),
      }, 25000, 'Preparing your preview took too long. Please try again.');
      const payload = await response.json().catch(() => ({}));
      if (!response.ok || !payload.personalization?.previewUrl) {
        throw new Error(payload.error || 'Could not prepare your personalized preview.');
      }
      return payload.personalization;
    };

    const formatMoney = (cents) => {
      const currency = String(window.Shopify?.currency?.active || 'USD');
      try {
        return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(numberValue(cents, 0) / 100);
      } catch (error) {
        return `$${(numberValue(cents, 0) / 100).toFixed(2)}`;
      }
    };

    const variantImageUrl = (variant) => String(
      variant?.featured_image?.src
      || variant?.featured_media?.preview_image?.src
      || '',
    );
    const activeBinding = () => {
      const variantId = String(selectedVariant?.id || '').split('/').pop();
      return (Array.isArray(config?.productFamily?.variantBindings) ? config.productFamily.variantBindings : [])
        .find((binding) => String(binding?.shopifyVariantId || '').split('/').pop() === variantId)
        || null;
    };
    const normalizedVariantOptionValue = (value) => (
      String(value || '').toLowerCase().replace(/[^a-z0-9]+/g, '')
    );
    const variantControllerValue = (field, binding) => {
      const key = String(fieldKey(field));
      const explicit = binding?.personalizationValues?.[key];
      if (explicit != null && String(explicit)) return String(explicit);
      const options = binding?.options && typeof binding.options === 'object' ? binding.options : {};
      const labelKey = normalizedVariantOptionValue(fieldLabel(field));
      const optionEntry = Object.entries(options).find(([name]) => {
        const optionKey = normalizedVariantOptionValue(name);
        return labelKey === optionKey || labelKey.endsWith(optionKey);
      });
      if (!optionEntry || !String(optionEntry[1] || '')) return '';
      const rawValue = String(optionEntry[1]);
      const allowed = hasChoices(field) ? field.values.map(optionValue).map(String).filter(Boolean) : [];
      if (!allowed.length) return rawValue;
      const rawKey = normalizedVariantOptionValue(rawValue);
      const styleValue = Object.entries(options)
        .find(([name]) => normalizedVariantOptionValue(name) === 'style')?.[1] || '';
      const combinedKey = normalizedVariantOptionValue(`${styleValue} ${rawValue}`);
      return allowed.find((value) => normalizedVariantOptionValue(value) === rawKey)
        || allowed.find((value) => normalizedVariantOptionValue(value) === combinedKey)
        || allowed.find((value) => {
          const valueKey = normalizedVariantOptionValue(value);
          return valueKey.endsWith(rawKey)
            && (!styleValue || valueKey.startsWith(normalizedVariantOptionValue(styleValue)));
        })
        || '';
    };
    const syncVariantPersonalizationState = () => {
      const binding = activeBinding();
      if (!binding) return [];
      const optionNames = Object.keys(binding.options || {}).map(normalizedVariantOptionValue);
      const changed = [];
      fields.forEach((field) => {
        const labelKey = normalizedVariantOptionValue(fieldLabel(field));
        const isVariantController = String(field.sourceLabel || '').toLowerCase().includes('variant_selector')
          || (field.hiddenFromShopper && optionNames.some((name) => labelKey === name || labelKey.endsWith(name)));
        if (!isVariantController) return;
        const value = variantControllerValue(field, binding);
        const key = fieldKey(field);
        if (!value || String(state[key] || '') === value) return;
        state[key] = value;
        changed.push(field);
      });
      return changed;
    };

    const setCartStatus = (message = '', stateName = '') => {
      if (!cartStatusNode) return;
      cartStatusNode.textContent = message;
      cartStatusNode.dataset.state = stateName;
    };

    const updateSelectedVariant = (shouldTrack = false) => {
      const selectedOptions = selectedProductOptions();
      selectedVariant = selectedOptions.length
        ? variants.find((variant) => selectedOptions.every((value, index) => String(variant.options?.[index] || '') === value))
        : variants.find((variant) => variant.available) || variants[0] || null;

      if (selectedVariant) {
        if (productPriceNode) productPriceNode.textContent = formatMoney(selectedVariant.price);
        const selectedImage = variantImageUrl(selectedVariant);
        if (selectedImage) {
          if (productPreviewNode) productPreviewNode.src = selectedImage;
          if (mainImageNode) mainImageNode.src = selectedImage;
          backgroundNode.src = selectedImage;
        }
        const hasComparePrice = numberValue(selectedVariant.compare_at_price, 0) > numberValue(selectedVariant.price, 0);
        if (comparePriceNode) {
          comparePriceNode.hidden = !hasComparePrice;
          comparePriceNode.textContent = hasComparePrice ? formatMoney(selectedVariant.compare_at_price) : '';
        }
      }
      const variantFieldsChanged = syncVariantPersonalizationState();
      if (shouldTrack && variantFieldsChanged.length) {
        renderControls();
        renderPreview('Product options updated');
      }

      const available = Boolean(selectedVariant?.available);
      if (ctaButton) ctaButton.disabled = productOptionsOpen && (!available || addingToCart);
      if (ctaLabel && !addingToCart) ctaLabel.textContent = productOptionsOpen && !available ? 'SOLD OUT' : 'ADD TO CART';
      setCartStatus(productOptionsOpen && !selectedVariant ? 'This option combination is unavailable.' : '', 'error');

      if (shouldTrack) {
        track('variant_select', {
          variant_id: String(selectedVariant?.id || ''),
          variant_title: String(selectedVariant?.title || ''),
          variant_price: numberValue(selectedVariant?.price, 0) / 100,
          variant_available: available,
        });
      }
      return selectedVariant;
    };

    const revealProductOptions = () => {
      if (!productOptionsPanel || productOptionsOpen) return;
      if (animationRunning) stopDemo();
      productOptionsOpen = true;
      productOptionsPanel.hidden = false;
      window.requestAnimationFrame(() => productOptionsPanel.classList.add('is-open'));
      updateSelectedVariant();
      track('options_open', {
        option_count: productOptionGroups().length,
        variant_count: variants.length,
      });
      productOptionsPanel.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    };

    const cartSections = (cart) => (
      typeof cart?.getSectionsToRender === 'function'
        ? cart.getSectionsToRender().map((section) => section.id)
        : []
    );

    const addSelectedVariantToCart = async () => {
      if (!selectedVariant?.available || addingToCart) return;
      addingToCart = true;
      ctaButton.disabled = true;
      ctaButton.classList.add('is-loading');
      ctaButton.setAttribute('aria-busy', 'true');
      ctaLabel.textContent = 'ADDING…';
      setCartStatus('Adding your item…', 'loading');
      const variantDetails = {
        variant_id: String(selectedVariant.id || ''),
        variant_title: String(selectedVariant.title || ''),
        variant_price: numberValue(selectedVariant.price, 0) / 100,
      };
      track('add_to_cart', variantDetails);

      try {
        setCartStatus('Preparing your preview…', 'loading');
        const personalization = await createPersonalization();
        const cartProperties = {
          _demo_source: 'article_personalization_demo',
          _demo_session_id: demoSessionId,
          _article_handle: String(root.dataset.articleHandle || ''),
          _wahup_personalization_id: String(personalization.id || ''),
          _wahup_personalization_signature: String(personalization.signature || ''),
          _wahup_template_revision: String(personalization.templateRevision || ''),
          _wahup_preview_url: String(personalization.previewUrl || ''),
        };
        personalizationOptionDetails().forEach((option) => {
          cartProperties[option.name] = option.value;
        });
        const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        const formData = new FormData();
        formData.append('id', String(selectedVariant.id));
        formData.append('quantity', '1');
        Object.entries(cartProperties).forEach(([name, value]) => {
          if (value) formData.append(`properties[${name}]`, value);
        });
        const sections = cartSections(cart);
        if (sections.length) formData.append('sections', sections.join(','));
        formData.append('sections_url', window.location.pathname);

        const response = await fetchWithTimeout(window.routes?.cart_add_url || '/cart/add.js', {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: formData,
        }, 15000, 'Adding to cart took too long. Please try again.');
        const result = await response.json();
        if (!response.ok || result.status) throw new Error(result.description || 'Could not add this item.');

        ctaLabel.textContent = 'ADDED';
        setCartStatus('Added to cart.', 'success');
        track('add_success', variantDetails);
        if (typeof window.renderCountInCart === 'function') window.renderCountInCart();
        if (typeof window.publish === 'function' && window.PUB_SUB_EVENTS?.cartUpdate) {
          window.publish(window.PUB_SUB_EVENTS.cartUpdate, { source: 'article-personalization-demo' });
        }
        stopDemo();
        root.classList.add('is-minimized');
        document.body.classList.remove('is-personalization-demo-open');
        open = false;
        if (cart && typeof cart.renderContents === 'function' && result.sections) {
          if (typeof cart.setActiveElement === 'function') cart.setActiveElement(ctaButton);
          cart.renderContents(result);
        }
        const popupItem = {
          ...result,
          product_title: result.product_title || root.dataset.productTitle || result.title || '',
          image: personalization.previewUrl || result.image || variantImageUrl(selectedVariant) || productPreviewNode?.src || '',
          price: numberValue(selectedVariant.price, result.price),
          final_line_price: numberValue(selectedVariant.price, result.price) * numberValue(result.quantity, 1),
          options_with_values: selectedProductOptionDetails(),
          properties: { ...(result.properties || {}), ...cartProperties },
        };
        window.dispatchEvent(new CustomEvent('cart:added', {
          detail: { source: 'article-personalization-demo', item: popupItem },
        }));
        window.dispatchEvent(new CustomEvent('cart:updated', {
          detail: { source: 'article-personalization-demo', result },
        }));
      } catch (error) {
        const message = String(error?.message || 'Could not add this item. Please try again.');
        setCartStatus(message, 'error');
        track('add_error', {
          ...variantDetails,
          error_message: message.slice(0, 100),
        });
        ctaLabel.textContent = 'TRY AGAIN';
      } finally {
        addingToCart = false;
        ctaButton.classList.remove('is-loading');
        ctaButton.removeAttribute('aria-busy');
        ctaButton.disabled = !selectedVariant?.available;
        if (open) {
          window.setTimeout(() => {
            if (!addingToCart && ctaLabel.textContent !== 'TRY AGAIN') ctaLabel.textContent = 'ADD TO CART';
          }, 1600);
        }
      }
    };

    const scrollDemoTargetIntoView = (target) => {
      const horizontalScroller = target.closest(
        '.article-personalization-demo__choices, .article-personalization-demo__variant-values',
      );
      if (horizontalScroller) {
        const targetRect = target.getBoundingClientRect();
        const rowRect = horizontalScroller.getBoundingClientRect();
        const horizontalDelta = targetRect.left + targetRect.width / 2 - (rowRect.left + rowRect.width / 2);
        if (Math.abs(horizontalDelta) > 2) {
          horizontalScroller.scrollBy({ left: horizontalDelta, behavior: 'smooth' });
        }
      }
      const scroller = root.querySelector('.article-personalization-demo__body');
      if (!scroller || !window.matchMedia('(max-width: 749px)').matches) {
        target.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
        return;
      }
      const anchor = target.closest('[data-demo-field], [data-personalization-demo-product-options]') || target;
      const scrollerRect = scroller.getBoundingClientRect();
      const previewRect = root.querySelector('.article-personalization-demo__preview-column')?.getBoundingClientRect();
      const footerRect = root.querySelector('.article-personalization-demo__footer')?.getBoundingClientRect();
      const anchorRect = anchor.getBoundingClientRect();
      const safeTop = Math.max(scrollerRect.top, previewRect?.bottom || scrollerRect.top) + 12;
      const safeBottom = Math.min(scrollerRect.bottom, footerRect?.top || scrollerRect.bottom) - 12;
      let delta = 0;
      if (anchorRect.top < safeTop) delta = anchorRect.top - safeTop;
      else if (anchorRect.bottom > safeBottom) delta = anchorRect.bottom - safeBottom;
      if (delta) scroller.scrollBy({ top: delta, behavior: 'smooth' });
    };

    const moveCursor = async (target, token) => {
      if (!target || token !== runToken || !open) return false;
      scrollDemoTargetIntoView(target);
      await wait(360);
      if (token !== runToken || !open) return false;
      const rect = target.getBoundingClientRect();
      const row = target.closest(
        '.article-personalization-demo__choices, .article-personalization-demo__variant-values',
      );
      const rowRect = row?.getBoundingClientRect();
      const visibleLeft = rowRect ? Math.max(rect.left, rowRect.left + 4) : rect.left;
      const visibleRight = rowRect ? Math.min(rect.right, rowRect.right - 4) : rect.right;
      const targetX = visibleRight > visibleLeft
        ? visibleLeft + (visibleRight - visibleLeft) * .58
        : rowRect ? rowRect.left + rowRect.width / 2 : rect.left + rect.width * .58;
      const x = Math.max(8, Math.min(window.innerWidth - 36, targetX));
      const y = Math.max(8, Math.min(window.innerHeight - 44, rect.top + rect.height * .56));
      const transform = `translate3d(${Math.round(x)}px, ${Math.round(y)}px, 0)`;
      cursorNode.style.setProperty('--demo-cursor-transform', transform);
      cursorNode.style.transform = transform;
      cursorNode.classList.add('is-visible');
      await wait(680);
      return token === runToken && open;
    };

    const cursorClick = async (target, token) => {
      if (!await moveCursor(target, token)) return false;
      cursorNode.classList.add('is-clicking');
      target.classList.add('is-demo-pressed');
      void playInteractionSound();
      await wait(180);
      cursorNode.classList.remove('is-clicking');
      target.classList.remove('is-demo-pressed');
      return token === runToken && open;
    };

    const demoText = (field) => {
      const label = fieldLabel(field).toLowerCase();
      if (field.demoText) return String(field.demoText);
      if (label.includes('name')) return 'JENNIFER';
      if (fieldHasKeyword(field, 'year')) return currentYearValue();
      if (label.includes('date')) return currentYearValue();
      if (label.includes('message') || label.includes('quote')) return 'LOVE YOU';
      return 'YOUR TEXT';
    };

    const showMainImage = (visible) => {
      root.classList.toggle('is-showing-main-image', visible);
      if (mainImageNode) mainImageNode.setAttribute('aria-hidden', String(!visible));
      if (compositionNode) compositionNode.setAttribute('aria-hidden', String(visible));
    };

    const resetRecreationState = async () => {
      state = Object.fromEntries(fields.map((field) => [fieldKey(field), initialValue(field)]));
      assignedState = Object.fromEntries(fields.map((field) => [fieldKey(field), assignedValue(field)]));
      syncVariantPersonalizationState();
      renderControls();
      await hydrateSelectedCustomilyAssets();
      renderPreview('Preparing assigned product');
    };

    const animateDemo = async (runSource = 'auto') => {
      const token = ++runToken;
      animationRunning = true;
      cursorNode.classList.remove('is-visible');
      await resetRecreationState();
      showMainImage(true);
      await wait(1100);
      if (token !== runToken || !open) return;
      showMainImage(false);
      await wait(420);

      const processed = new Set();
      for (let pass = 0; pass < fields.length * 2; pass += 1) {
        if (token !== runToken || !open) break;
        const field = visibleFields().find((candidate) => !processed.has(fieldKey(candidate)));
        if (!field) break;
        const key = fieldKey(field);
        processed.add(key);
        const wrapper = root.querySelector(`[data-demo-field="${CSS.escape(key)}"]`);
        if (!wrapper) continue;
        wrapper.classList.add('is-demo-active');
        if (isUploadField(field)) {
          const button = wrapper.querySelector('[data-demo-upload]');
          if (await cursorClick(button, token)) {
            button.classList.add('is-clicked');
            const target = String(assignedState[key] || '');
            if (target) setValue(key, target, `${fieldLabel(field)} selected`);
            else setPreviewStatus(`${fieldLabel(field)} selected`);
            await wait(700);
            button.classList.remove('is-clicked');
          }
        } else if (hasChoices(field)) {
          const buttons = [...wrapper.querySelectorAll('[data-demo-choice]')];
          const targetValue = String(assignedState[key] || state[key] || '');
          const target = buttons.find((button) => button.dataset.value === targetValue) || buttons[0];
          const resolvedTargetValue = String(target?.dataset.value || '');
          const currentValue = String(state[key] || '');
          const alternate = buttons.find((button) => (
            button.dataset.value !== resolvedTargetValue
            && button.dataset.value !== currentValue
          )) || buttons.find((button) => button.dataset.value !== resolvedTargetValue);
          const alternateValue = String(alternate?.dataset.value || '');
          const choiceSequence = alternateValue
            ? alternateValue === currentValue && currentValue !== resolvedTargetValue
              ? [resolvedTargetValue, alternateValue, resolvedTargetValue]
              : [alternateValue, resolvedTargetValue]
            : [resolvedTargetValue];
          for (const value of choiceSequence) {
            const currentWrapper = root.querySelector(`[data-demo-field="${CSS.escape(key)}"]`);
            const button = currentWrapper?.querySelector(`[data-demo-choice][data-value="${CSS.escape(value)}"]`);
            if (!button || !await cursorClick(button, token)) break;
            currentWrapper.classList.add('is-demo-active');
            await setValueAndHydrate(key, value, `${fieldLabel(field)}: ${button.getAttribute('aria-label') || ''}`);
            await wait(value === resolvedTargetValue ? 620 : 460);
          }
        } else {
          const input = wrapper.querySelector('[data-demo-text]');
          if (await cursorClick(input, token)) {
            const sample = String(assignedState[key] || demoText(field));
            state[key] = '';
            const layersBeforeTyping = resolvedLayers();
            input.value = '';
            input.classList.add('is-typing');
            for (const character of sample) {
              if (token !== runToken || !open) break;
              state[key] += character;
              input.value = state[key];
              renderPreview(`${fieldLabel(field)}: ${state[key]}`);
              await wait(105);
            }
            input.classList.remove('is-typing');
            const layersAfterTyping = resolvedLayers();
            await focusChangedPreviewLayers(changedPreviewLayers(layersBeforeTyping, layersAfterTyping));
            await wait(650);
          }
        }
        wrapper.classList.remove('is-demo-active');
        root.querySelector(`[data-demo-field="${CSS.escape(key)}"]`)?.classList.remove('is-demo-active');
      }
      if (token === runToken && open) {
        setPreviewStatus('Your design is ready');
        track('complete', {
          run_source: runSource,
          field_count: processed.size,
        });
        const cta = root.querySelector('[data-personalization-demo-cta]');
        await moveCursor(cta, token);
        await wait(900);
      }
      cursorNode.classList.remove('is-visible');
      animationRunning = false;
    };

    const stopDemo = () => {
      runToken += 1;
      animationRunning = false;
      cursorNode.classList.remove('is-visible', 'is-clicking');
      showMainImage(false);
      root.querySelectorAll('.is-demo-active').forEach((element) => element.classList.remove('is-demo-active'));
    };

    const syncGiftViewport = () => {
      if (!giftModal || giftModal.hidden) return;
      window.cancelAnimationFrame(giftViewportFrame);
      giftViewportFrame = window.requestAnimationFrame(() => {
        const viewport = window.visualViewport;
        const viewportTop = Math.max(0, Number(viewport?.offsetTop) || 0);
        const viewportHeight = Math.max(240, Number(viewport?.height) || window.innerHeight);
        giftModal.style.setProperty('--article-gift-viewport-top', `${viewportTop}px`);
        giftModal.style.setProperty('--article-gift-viewport-height', `${viewportHeight}px`);
        giftModal.classList.toggle('is-keyboard-open', viewportHeight < window.innerHeight - 120);
      });
    };

    const closeGiftModal = () => {
      if (!giftModal || giftModal.hidden) return;
      window.cancelAnimationFrame(giftViewportFrame);
      giftModal.classList.remove('is-open');
      giftModal.classList.remove('is-keyboard-open');
      giftModal.hidden = true;
      giftModal.setAttribute('aria-hidden', 'true');
      giftModal.style.removeProperty('--article-gift-viewport-top');
      giftModal.style.removeProperty('--article-gift-viewport-height');
    };

    const openGiftModal = () => {
      if (!giftModal) return;
      if (animationRunning) stopDemo();
      giftModal.hidden = false;
      giftModal.setAttribute('aria-hidden', 'false');
      syncGiftViewport();
      window.requestAnimationFrame(() => giftModal.classList.add('is-open'));
      giftModal.querySelector('input[type="email"]')?.focus({ preventScroll: true });
      window.setTimeout(syncGiftViewport, 80);
      window.setTimeout(syncGiftViewport, 320);
      track('gift_open');
    };

    const submitGiftPromotion = async () => {
      if (!giftForm || giftSubmitting) return;
      const submit = giftForm.querySelector('button[type="submit"]');
      const errorNode = giftForm.querySelector('[data-personalization-demo-gift-error]');
      giftSubmitting = true;
      if (errorNode) errorNode.hidden = true;
      if (submit) {
        submit.disabled = true;
        submit.textContent = 'PLEASE WAIT...';
      }
      try {
        const email = String(new FormData(giftForm).get('email') || '');
        const response = await fetch(giftPromotionApi, {
          method: 'POST',
          mode: 'cors',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email,
            shop: root.dataset.shop || '',
            productHandle: root.dataset.productHandle || '',
          }),
        });
        const promotion = await response.json().catch(() => ({}));
        if (!response.ok || !promotion.code || promotion.sent !== true || promotion.subscribed !== true) {
          throw new Error(promotion.error || 'Promotion email delivery failed');
        }
        const codeNode = giftModal?.querySelector('.article-personalization-demo__gift-code');
        if (codeNode) codeNode.textContent = promotion.code;
        const offerNode = giftModal?.querySelector('[data-personalization-demo-gift-offer]');
        const successNode = giftModal?.querySelector('[data-personalization-demo-gift-success]');
        if (offerNode) offerNode.hidden = true;
        if (successNode) successNode.hidden = false;
        track('gift_success');
      } catch (error) {
        if (errorNode) {
          errorNode.textContent = error instanceof Error ? error.message : 'We could not prepare your discount. Please try again.';
          errorNode.hidden = false;
        }
        track('gift_error');
      } finally {
        giftSubmitting = false;
        if (submit) {
          submit.disabled = false;
          submit.textContent = 'SHOW MY CODE';
        }
      }
    };

    const closeModal = (closeMethod = 'button') => {
      if (!open) return;
      track('minimize', {
        close_method: closeMethod,
        demo_running: animationRunning,
      });
      stopDemo();
      closeGiftModal();
      window.clearTimeout(openTransitionTimer);
      root.classList.remove('is-opening');
      root.classList.add('is-minimized');
      document.body.classList.remove('is-personalization-demo-open');
      open = false;
    };

    const openModal = (openSource = 'auto') => {
      if (open || !fields.length) return;
      open = true;
      window.clearTimeout(autoOpenTimer);
      returnScrollCleanup();
      root.hidden = false;
      window.clearTimeout(openTransitionTimer);
      root.classList.add('is-opening');
      document.body.classList.add('is-personalization-demo-open');
      window.requestAnimationFrame(() => {
        root.classList.remove('is-minimized');
        root.classList.add('is-open');
        dialogNode.focus({ preventScroll: true });
        track(openSource === 'launcher' ? 'reopen' : 'view', {
          field_count: visibleFields().length,
        });
        if (requiresSoundStartGesture && !interactionSoundUnlockedByGesture) {
          stopDemo();
          showMainImage(true);
          soundStartButton.hidden = false;
          root.dataset.walkthroughSoundGate = 'waiting';
        } else {
          animateDemo(openSource);
        }
        openTransitionTimer = window.setTimeout(() => root.classList.remove('is-opening'), 1000);
      });
    };

    controlsNode.addEventListener('click', (event) => {
      const choice = event.target.closest('[data-demo-choice]');
      const upload = event.target.closest('[data-demo-upload]');
      if (!choice && !upload) return;
      if (animationRunning) stopDemo();
      if (choice) {
        const key = choice.dataset.demoChoice || '';
        const label = root.querySelector(`[data-demo-field="${CSS.escape(key)}"] .article-personalization-demo__label`)?.firstChild?.textContent?.trim() || 'Option';
        const value = choice.dataset.value || '';
        setValueAndHydrate(key, value, `${label}: ${choice.getAttribute('aria-label') || ''}`);
        track('option_select', {
          option_id: key,
          option_label: label,
          option_value: value,
        });
      }
      if (upload) {
        upload.classList.add('is-clicked');
        window.setTimeout(() => upload.classList.remove('is-clicked'), 600);
        track('upload_click', {
          option_id: upload.dataset.demoUpload || '',
        });
      }
    });

    controlsNode.addEventListener('input', (event) => {
      const input = event.target.closest('[data-demo-text]');
      if (!input) return;
      if (animationRunning) stopDemo();
      const key = input.dataset.demoText || '';
      setValue(key, input.value, `${input.getAttribute('aria-label') || 'Text'}: ${input.value}`);
    });

    controlsNode.addEventListener('change', (event) => {
      const input = event.target.closest('[data-demo-text]');
      if (!input) return;
      track('text_change', {
        option_id: input.dataset.demoText || '',
        option_label: input.getAttribute('aria-label') || 'Text',
        text_length: input.value.length,
      });
    });

    productOptionsPanel?.addEventListener('click', (event) => {
      const button = event.target.closest('[data-personalization-demo-product-value]');
      if (!button) return;
      const group = button.closest('[data-personalization-demo-product-option]');
      group?.querySelectorAll('[data-personalization-demo-product-value]').forEach((choice) => {
        const selected = choice === button;
        choice.classList.toggle('is-selected', selected);
        choice.setAttribute('aria-pressed', String(selected));
      });
      updateSelectedVariant(true);
    });

    document.addEventListener('pointerdown', unlockInteractionSound, { passive: true });
    document.addEventListener('touchstart', unlockInteractionSound, { passive: true });
    document.addEventListener('keydown', unlockInteractionSound);
    root.addEventListener('touchstart', playTouchedInteractionSound, { passive: true, capture: true });
    root.addEventListener('click', (event) => {
      if (event.target.closest('button, [data-demo-text]')) void playInteractionSound();
    });

    soundStartButton?.addEventListener('click', async (event) => {
      event.preventDefault();
      const nativePlayback = playInteractionSound();
      const audioContext = await resumeInteractionAudioContext();
      if (audioContext) await prepareInteractionSound(audioContext);
      const nativePlayed = await nativePlayback;
      interactionSoundUnlockedByGesture = Boolean(nativePlayed || (audioContext && audioContext.state === 'running'));
      root.dataset.walkthroughSoundGate = interactionSoundUnlockedByGesture ? 'unlocked' : 'blocked';
      if (!interactionSoundUnlockedByGesture) return;
      soundStartButton.hidden = true;
      animateDemo('sound_start');
    });

    giftButton?.addEventListener('click', openGiftModal);
    window.visualViewport?.addEventListener('resize', syncGiftViewport, { passive: true });
    window.visualViewport?.addEventListener('scroll', syncGiftViewport, { passive: true });
    window.addEventListener('resize', syncGiftViewport, { passive: true });
    giftModal?.addEventListener('click', (event) => {
      if (event.target === giftModal || event.target.closest('[data-personalization-demo-gift-close]')) {
        closeGiftModal();
      }
    });
    giftForm?.addEventListener('submit', (event) => {
      event.preventDefault();
      submitGiftPromotion();
    });

    root.querySelectorAll('[data-personalization-demo-close]').forEach((button) => button.addEventListener('click', () => {
      closeModal(button.classList.contains('article-personalization-demo__backdrop') ? 'backdrop' : 'button');
    }));
    reopenButton.addEventListener('click', () => openModal('launcher'));
    ctaButton?.addEventListener('click', () => {
      track('cta_click', {
        cta_state: productOptionsOpen ? 'add' : 'open_options',
      });
      if (!productOptionsOpen) {
        revealProductOptions();
        return;
      }
      addSelectedVariantToCart();
    });
    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      if (giftModal && !giftModal.hidden) {
        closeGiftModal();
        return;
      }
      if (open) closeModal('escape');
    });

    const initialize = async () => {
      const initializeStartedAt = window.performance?.now?.() || Date.now();
      const preloadAudioContext = ensureInteractionAudioContext();
      if (preloadAudioContext) void prepareInteractionSound(preloadAudioContext);
      if (root.dataset.shop && (root.dataset.productId || root.dataset.productHandle)) {
        try {
          const response = await fetch(`https://custom.wahup.com/api/storefront/customily-config?${new URLSearchParams({
            shop: root.dataset.shop,
            productId: root.dataset.productId,
            handle: root.dataset.productHandle,
          })}`, { mode: 'cors' });
          const payload = await response.json();
          if (response.ok && payload.config) config = payload.config;
        } catch (error) {
          // Embedded metafield config remains the offline-safe fallback.
        }
      }
      await loadPreviewFonts();
      fields = allFields();
      state = Object.fromEntries(fields.map((field) => [fieldKey(field), initialValue(field)]));
      assignedState = Object.fromEntries(fields.map((field) => [fieldKey(field), assignedValue(field)]));
      renderControls();
      applySavedVariant();
      updateSelectedVariant();
      await hydrateSelectedCustomilyAssets();
      renderPreview('Choose an option to begin');
      showMainImage(true);
      const delay = Math.max(0, numberValue(root.dataset.delaySeconds, 4)) * 1000;
      const elapsed = (window.performance?.now?.() || Date.now()) - initializeStartedAt;
      let lastScrollY = Math.max(0, window.scrollY);
      let furthestScrollY = lastScrollY;
      let upwardTravel = 0;
      const handleReturnScroll = () => {
        const currentScrollY = Math.max(0, window.scrollY);
        furthestScrollY = Math.max(furthestScrollY, currentScrollY);
        if (currentScrollY < lastScrollY - 1 && furthestScrollY >= 120) {
          upwardTravel += lastScrollY - currentScrollY;
        } else if (currentScrollY > lastScrollY + 1) {
          upwardTravel = 0;
        }
        lastScrollY = currentScrollY;
        if (upwardTravel >= 48) openModal('return_scroll');
      };
      window.addEventListener('scroll', handleReturnScroll, { passive: true });
      returnScrollCleanup = () => window.removeEventListener('scroll', handleReturnScroll);
      autoOpenTimer = window.setTimeout(() => openModal('auto'), Math.max(0, delay - elapsed));
    };

    initialize();
  });
})();
